import { useEffect, useMemo, useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import {
  PlusCircleSolidIcon,
  ActionBar,
  Button,
  Field,
  FormCard,
  Modal,
  SectionHeader,
  type Column,
} from "@fawkes/ui";

import type { EntityColumnMeta, EntityMeta, EntityPage, EntityRow } from "./descriptor";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

// Entity-admin migration (Jmix-exit): the generic replacement for a Jmix generated `*ListView` +
// `*DetailView` pair. One component renders ANY docasoft entity's list + create/edit/delete from its
// `_meta` descriptor (GET /api/entity-admin/{entity}/_meta) — the framework the whole `view/docasoft/**`
// backlog rolls out on. List columns + form fields come straight from the descriptor, which mirrors the
// Jmix view XML column/field set, so the migrated screen shows the same fields as the legacy one.

const PAGE_SIZE = 25;

/** Format a raw cell value for the list grid: dd-MM-yyyy dates, Ja/Nee booleans, "-" for empty. */
function formatCell(value: unknown, type: EntityColumnMeta["type"]): string {
  if (value === null || value === undefined || value === "") return "-";
  if (type === "boolean") return value ? "Ja" : "Nee";
  if (type === "date" || type === "datetime") {
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(String(value));
    if (m) return m[4] ? `${m[3]}-${m[2]}-${m[1]} ${m[4]}:${m[5]}` : `${m[3]}-${m[2]}-${m[1]}`;
  }
  return String(value);
}

/** The HTML input type for a form field's logical type. */
function inputType(type: EntityColumnMeta["type"]): "text" | "number" | "date" | "datetime-local" {
  switch (type) {
    case "integer":
    case "decimal":
      return "number";
    case "date":
      return "date";
    case "datetime":
      return "datetime-local";
    default:
      return "text";
  }
}

type FormValues = Record<string, string | boolean>;

/** Seed a form from a row (edit) or empty (create): booleans stay boolean, everything else a string. */
function seedForm(columns: EntityColumnMeta[], row: EntityRow | null): FormValues {
  const out: FormValues = {};
  for (const c of columns) {
    if (!c.inForm) continue;
    const v = row ? row[c.name] : null;
    out[c.name] = c.type === "boolean" ? Boolean(v) : v === null || v === undefined ? "" : String(v);
  }
  return out;
}

/** Turn the form back into a JSON body: numbers as numbers, blanks as null, booleans as booleans. */
function toBody(columns: EntityColumnMeta[], form: FormValues): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const c of columns) {
    if (!c.inForm || c.readOnly) continue;
    const raw = form[c.name];
    if (c.type === "boolean") {
      body[c.name] = Boolean(raw);
    } else if (raw === "" || raw === undefined) {
      body[c.name] = null;
    } else if (c.type === "integer" || c.type === "decimal") {
      body[c.name] = Number(raw);
    } else {
      body[c.name] = raw;
    }
  }
  return body;
}

interface EditState {
  mode: "create" | "edit";
  id?: string;
}

/**
 * The generic entity-admin screen for one docasoft entity (keyed by `entity`). Fetches the `_meta`
 * descriptor, then renders the list (filter + grid + pagination) and a create/edit modal + delete
 * confirm — the Jmix genericFilter/dataGrid/list_create/list_edit/list_remove + detail_saveClose flow.
 */
export function EntityAdmin({ entity }: { entity: string }) {
  const t = useTranslate();
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [editing, setEditing] = useState<EditState | null>(null);
  const [form, setForm] = useState<FormValues>({});
  const [version, setVersion] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EntityRow | null>(null);
  const [notice, setNotice] = useState<"conflict" | "error" | null>(null);

  useEffect(() => {
    const h = setTimeout(() => { setDebounced(query.trim()); setPage(1); }, 300);
    return () => clearTimeout(h);
  }, [query]);

  // A new entity resets the list state so switching screens never lands on a stale page/filter.
  useEffect(() => { setPage(1); setQuery(""); setDebounced(""); setEditing(null); }, [entity]);

  const { data: metaData, isLoading: metaLoading, isError: metaError } = useCustom<EntityMeta>({
    url: `entity-admin/${entity}/_meta`,
    method: "get",
  });
  const meta = metaData?.data;

  const listUrl = useMemo(() => {
    const p = new URLSearchParams();
    if (debounced) p.set("q", debounced);
    p.set("page", String(page));
    p.set("size", String(PAGE_SIZE));
    return `entity-admin/${entity}?${p.toString()}`;
  }, [entity, debounced, page]);

  const { data: listData, isLoading: listLoading, isError: listError, refetch } = useCustom<EntityPage>({
    url: listUrl,
    method: "get",
    queryOptions: { enabled: meta !== undefined },
  });

  const { mutate, isLoading: saving } = useCustomMutation();

  if (metaLoading) return <p className="fawkes-muted">{t("common.loading")}</p>;
  if (metaError || !meta) return <p className="fawkes-error">{t("entityAdmin.loadFailed")}</p>;

  const idOf = (row: EntityRow) => String(row[meta.idColumn]);
  const listCols = meta.columns.filter((c) => c.inList);
  const formCols = meta.columns.filter((c) => c.inForm);
  const rows = listData?.data?.rows ?? [];
  const total = listData?.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns: Column<EntityRow>[] = listCols.map((c) => ({
    header: c.label,
    align: c.type === "integer" || c.type === "decimal" ? "right" : "left",
    cell: (row: EntityRow) => formatCell(row[c.name], c.type),
  }));

  const openCreate = () => {
    setNotice(null);
    setForm(seedForm(meta.columns, null));
    setVersion(null);
    setEditing({ mode: "create" });
  };

  const openEdit = (row: EntityRow) => {
    setNotice(null);
    setForm(seedForm(meta.columns, row));
    const v = row["version"];
    setVersion(typeof v === "number" ? v : v != null ? Number(v) : null);
    setEditing({ mode: "edit", id: idOf(row) });
  };

  const closeEdit = () => { setEditing(null); setNotice(null); };

  const save = () => {
    if (!editing) return;
    setNotice(null);
    const body = toBody(meta.columns, form);
    if (editing.mode === "edit" && version !== null) body["version"] = version;
    mutate(
      {
        url: editing.mode === "create" ? `entity-admin/${entity}` : `entity-admin/${entity}/${editing.id}`,
        method: editing.mode === "create" ? "post" : "put",
        values: body,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => { closeEdit(); refetch(); },
        onError: (err) => setNotice((err as { statusCode?: number }).statusCode === 409 ? "conflict" : "error"),
      },
    );
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    const id = idOf(deleteTarget);
    mutate(
      {
        url: `entity-admin/${entity}/${id}`,
        method: "delete",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => { setDeleteTarget(null); refetch(); }, onError: () => setDeleteTarget(null) },
    );
  };

  return (
    <FormCard>
      <SectionHeader>{meta.label}</SectionHeader>

      <div className="fawkes-entityadmin__toolbar">
        <input
          className="fui-field__input fawkes-entityadmin__search"
          placeholder={t("entityAdmin.search")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Button variant="primary" onClick={openCreate}><PlusCircleSolidIcon width={18} height={18} /> {t("entityAdmin.create")}</Button>
      </div>

      {listError ? (
        <p className="fawkes-error">{t("entityAdmin.loadFailed")}</p>
      ) : (
        <IndeelbareTabel tabelKey="entityAdmin-entity-admin"
          columns={[
            ...columns,
            {
              header: "",
              align: "right",
              cell: (row: EntityRow) => (
                <span className="fawkes-entityadmin__row-actions">
                  <Button variant="secondary" onClick={() => openEdit(row)}>{t("entityAdmin.edit")}</Button>
                  <Button variant="secondary" onClick={() => setDeleteTarget(row)}>{t("entityAdmin.delete")}</Button>
                </span>
              ),
            },
          ]}
          rows={rows}
          getRowKey={idOf}
          onRowDoubleClick={openEdit}
          empty={listLoading ? t("common.loading") : t("entityAdmin.empty")}
        />
      )}

      <div className="fawkes-entityadmin__pagination">
        <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
          {t("entityAdmin.prev")}
        </Button>
        <span className="fawkes-entityadmin__pageinfo">{t("entityAdmin.pageOf", { args: [String(page), String(pageCount)] })}</span>
        <Button variant="secondary" disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>
          {t("entityAdmin.next")}
        </Button>
      </div>

      {editing && (
        <Modal
          open
          title={editing.mode === "create" ? t("entityAdmin.createTitle", { args: [meta.label] }) : t("entityAdmin.editTitle", { args: [meta.label] })}
          onClose={closeEdit}
          footer={
            <>
              <Button variant="secondary" onClick={closeEdit}>{t("common.cancel")}</Button>
              <Button variant="primary" onClick={save} disabled={saving}>{t("common.save")}</Button>
            </>
          }
        >
          <div className="fawkes-entityadmin__form">
            {formCols.map((c) =>
              c.type === "boolean" ? (
                <label key={c.name} className="fui-field fawkes-entityadmin__check">
                  <span className="fui-field__label">{c.label}</span>
                  <input
                    type="checkbox"
                    checked={Boolean(form[c.name])}
                    disabled={c.readOnly}
                    onChange={(e) => setForm((f) => ({ ...f, [c.name]: e.target.checked }))}
                  />
                </label>
              ) : c.type === "choice" ? (
                // A closed value set (the api rejects anything else with a 400). Rendered as a select so
                // a discriminator like `shelter_intake_option` cannot be mistyped into a silently
                // different wizard flow. The blank option is always offered: an unset column is a
                // legitimate state (intake reads it as DEFAULT), so it must stay reachable from the UI.
                <label key={c.name} className="fui-field">
                  <span className="fui-field__label">{c.label}</span>
                  <select
                    className="fui-field__input"
                    value={String(form[c.name] ?? "")}
                    disabled={c.readOnly}
                    onChange={(e) => setForm((f) => ({ ...f, [c.name]: e.target.value }))}
                  >
                    <option value="">{t("entityAdmin.choiceBlank")}</option>
                    {(c.choices ?? []).map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                    {/* A value already stored that is no longer offered stays visible rather than
                        silently re-selecting the blank option and rewriting the row on the next save. */}
                    {form[c.name] && !(c.choices ?? []).includes(String(form[c.name])) && (
                      <option value={String(form[c.name])}>{String(form[c.name])}</option>
                    )}
                  </select>
                </label>
              ) : c.type === "longtext" ? (
                // Unbounded NVARCHAR(MAX) free text — the Jmix screen shows a text area, not a one-liner.
                <label key={c.name} className="fui-field">
                  <span className="fui-field__label">{c.label}</span>
                  <textarea
                    className="fui-field__input fawkes-entityadmin__textarea"
                    value={String(form[c.name] ?? "")}
                    disabled={c.readOnly}
                    onChange={(e) => setForm((f) => ({ ...f, [c.name]: e.target.value }))}
                  />
                </label>
              ) : (
                <Field
                  key={c.name}
                  label={c.label}
                  type={inputType(c.type)}
                  value={String(form[c.name] ?? "")}
                  disabled={c.readOnly}
                  maxLength={c.maxLength ?? undefined}
                  onChange={(e) => setForm((f) => ({ ...f, [c.name]: e.target.value }))}
                />
              ),
            )}
            {notice && (
              <span className="fawkes-error" role="alert">
                {t(notice === "conflict" ? "entityAdmin.saveConflict" : "entityAdmin.saveError")}
              </span>
            )}
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <Modal
          open
          title={t("entityAdmin.deleteTitle")}
          onClose={() => setDeleteTarget(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setDeleteTarget(null)}>{t("common.cancel")}</Button>
              <Button variant="primary" onClick={confirmDelete} disabled={saving}>{t("entityAdmin.delete")}</Button>
            </>
          }
        >
          <p>{t("entityAdmin.deleteBody")}</p>
        </Modal>
      )}

      <ActionBar>
        <span className="fawkes-muted">{t("entityAdmin.rowCount", { args: [String(total)] })}</span>
      </ActionBar>
    </FormCard>
  );
}
